import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useEffect, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";

import { clearMessage } from "../../../redux/actions/message";

import ProtectedRoutes from "../../../routes/ProtectedRoutes";
import PublicRoutes from "../../../routes/PublicRoutes";
import StorageItem from "../../StorageItems";
import StorageCategory from "../../StorageCategories";
import EditListing from "../../Spaces/EditListing";
import SubSpaces from "../../Spaces/SubSpaces";
import ImportSpaces from "../../Spaces/ImportSpaces";
import PromoCode from "../../PromoCodes";
import AddReviewForm from "../../Reviews/AddReviewForm";
import Review from "../../Reviews/Add";
import PayoutListing from "../../Payouts";
import PayoutDetailsView from "../../Payouts/ViewIndex";
import RefundListing from "../../Refunds";
import RefundDetailsView from "../../Refunds/ViewIndex";

const Login = lazy(() => import("../../Login/"));
const Dashboard = lazy(() => import("../../Dashboard/"));
const ForgotPassword = lazy(() => import("../../ForgotPassword/"));
const ResetPassword = lazy(() => import("../../ResetPassword/"));
const AdminList = lazy(() => import("../../Admins/List"));
const Admin = lazy(() => import("../../Admins/Admin"));
const UserList = lazy(() => import("../../Users/All"));
// const Hosts = lazy(() => import("../../Users/Hosts"));
// const Renters = lazy(() => import("../../Users/Renters"));
const User = lazy(() => import("../../Users/User"));
const PartnerAgreements = lazy(() => import("../../Users/PartnerAgreements"));
const CSR = lazy(() => import("../../CSRs/CSR"));
const CSRList = lazy(() => import("../../CSRs/List"));
const Profile = lazy(() => import("../../Profile"));
const CityList = lazy(() => import("../../Cities/List"));
const City = lazy(() => import("../../Cities/index"));
const DynamicMetaDataList = lazy(() => import("../../DynamicMetaData/List"));
const DynamicMetaData = lazy(() => import("../../DynamicMetaData/index"));
const ReviewList = lazy(() => import("../../Reviews/List"));
const ReviewView = lazy(() => import("../../Reviews/index"));
const AllReservationList = lazy(() => import("../../Reservations/List"));
const ReservationList = lazy(() => import("../../Reservations/ListByUser"));
const TransactionList = lazy(() => import("../../Reservations/TransactionList"));
const PayoutList = lazy(() => import("../../Reservations/PayoutList"));
const RefundList = lazy(() => import("../../Reservations/RefundList"));
const ReservationView = lazy(() => import("../../Reservations/index"));
const AddReservation = lazy(() => import("../../Reservations/Add"));
const SpaceList = lazy(() => import("../../Spaces/List"));
const SpaceView = lazy(() => import("../../Spaces/View"));
const UpdatePrices = lazy(() => import("../../Spaces/UpdatePrices"));
const StorageTypeList = lazy(() => import("../../StorageTypes/List"));
const PromoCodesList = lazy(() => import("../../PromoCodes/List"));
const StorageItemList = lazy(() => import("../../StorageItems/List"));
const StorageCategoryList = lazy(() => import("../../StorageCategories/List"));
const StorageType = lazy(() => import("../../StorageTypes/index"));
const FAQList = lazy(() => import("../../FAQ/List"));
const FAQ = lazy(() => import("../../FAQ/index"));
const SettingsConfig = lazy(() => import("../../Settings/Config"));
const SettingsGeneral = lazy(() => import("../../Settings/General"));
const SettingsBranding = lazy(() => import("../../Settings/Branding"));
const PageList = lazy(() => import("../../Pages/List"));
const Page = lazy(() => import("../../Pages/Page"));
const BecomeAHost = lazy(() => import("../../Pages/BecomeAHost"));
const SourceList = lazy(() => import("../../Sources/List"));
const Source = lazy(() => import("../../Sources/index"));
const HostPayouts = lazy(() => import("../../Payouts/List"));
const RenterPayments = lazy(() => import("../../Transactions/List"));
const AllTransactions = lazy(() => import("../../Transactions/AllTransactions"));
const LeadsTable = lazy(() => import("../../Leads/index"));
const RewardPointsList = lazy(() => import("../../RewardPoints/List"));
const RewardPointsDetailsView = lazy(() => import("../../RewardPoints/ViewIndex"));
const SpotList = lazy(() => import("../../Spots/List"));
const AddUpdateSpot = lazy(() => import("../../Spots/index"));
const BulkAddSpots = lazy(() => import("../../Spots/BulkAdd"));
const PILotList = lazy(() => import("../../ParkingInventory/Lots/List"));
const PIAddUpdateLot = lazy(() => import("../../ParkingInventory/Lots/AddUpdateLotForm"));
const PISpotList = lazy(() => import("../../ParkingInventory/Spots/List"));
const PIAddUpdateSpot = lazy(() => import("../../ParkingInventory/Spots/AddUpdateSpotForm"));
const PISpotDetail = lazy(() => import("../../ParkingInventory/Spots/SpotDetail"));
const PIReservationList = lazy(() => import("../../ParkingInventory/Reservations/List"));
const BlockedIpAddressList = lazy(() => import("../../BlockedIpAddresses/List"));
const SmsLogs = lazy(() => import("../../SmsLogs/index"));
const SmsLogView = lazy(() => import("../../SmsLogs/View"));

const AppMain = () => {
  let isLoggedIn = false;

  const { user: currentUser } = useSelector((state) => state.auth);

  if (currentUser && (currentUser.roleId === 1 || currentUser.roleId === 2)) {
    isLoggedIn = true;
  }
  const dispatch = useDispatch();

  let location = useLocation();

  useEffect(() => {
    if (["/login"].includes(location.pathname)) {
      dispatch(clearMessage()); // clear message when changing location
    }
  }, [dispatch, location]);

  return (
    <Routes>
      <Route element={<ProtectedRoutes isLoggedIn={isLoggedIn} />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="page">
          <Route path="list" element={<PageList currentUser={currentUser} />} />
          <Route path="add" element={<Page currentUser={currentUser} />} />
          <Route path=":id" element={<Page currentUser={currentUser} />} />
          <Route path="become-a-host" element={<BecomeAHost currentUser={currentUser} />} />
        </Route>
        <Route path="settings">
          <Route path="general" element={<SettingsGeneral currentUser={currentUser} />} />
          <Route path="config" element={<SettingsConfig currentUser={currentUser} />} />
          <Route path="branding" element={<SettingsBranding currentUser={currentUser} />} />
        </Route>
        <Route path="city">
          <Route path="list" element={<CityList currentUser={currentUser} />} />
          <Route path="add" element={<City currentUser={currentUser} />} />
          <Route path=":id" element={<City currentUser={currentUser} />} />
        </Route>
        <Route path="dynamicmetadata">
          <Route path="list" element={<DynamicMetaDataList currentUser={currentUser} />} />
          <Route path="add" element={<DynamicMetaData currentUser={currentUser} />} />
          <Route path=":id" element={<DynamicMetaData currentUser={currentUser} />} />
        </Route>
        <Route path="spot">
          <Route path="list" element={<SpotList currentUser={currentUser} />} />
          <Route path="add" element={<AddUpdateSpot currentUser={currentUser} />} />
          <Route path="bulk-add" element={<BulkAddSpots currentUser={currentUser} />} />
          <Route path=":id" element={<AddUpdateSpot currentUser={currentUser} />} />
        </Route>
        <Route path="review">
          <Route path="add" element={<Review currentUser={currentUser} />} />
          <Route path="list" element={<ReviewList currentUser={currentUser} />} />
          <Route path=":id" element={<ReviewView currentUser={currentUser} />} />
        </Route>
        <Route path="storageType">
          <Route path="list" element={<StorageTypeList currentUser={currentUser} />} />
          <Route path="add" element={<StorageType currentUser={currentUser} />} />
          <Route path=":id" element={<StorageType currentUser={currentUser} />} />
        </Route>
        <Route path="promoCodes">
          <Route path="list" element={<PromoCodesList currentUser={currentUser} />} />
          <Route path="add" element={<PromoCode currentUser={currentUser} />} />
          <Route path=":id" element={<PromoCode currentUser={currentUser} />} />
        </Route>
        {/* Vehicle types now live in the Storage Items table — redirect any
            old /vehicleType links there instead of 404ing. */}
        <Route path="vehicleType">
          <Route path="list" element={<Navigate to="/storageItem/list" replace />} />
          <Route path="add" element={<Navigate to="/storageItem/add" replace />} />
          <Route path=":id" element={<Navigate to="/storageItem/list" replace />} />
        </Route>
        <Route path="storageItem">
          <Route path="list" element={<StorageItemList currentUser={currentUser} />} />
          <Route path="add" element={<StorageItem currentUser={currentUser} />} />
          <Route path=":id" element={<StorageItem currentUser={currentUser} />} />
        </Route>
        <Route path="storageCategory">
          <Route path="list" element={<StorageCategoryList currentUser={currentUser} />} />
          <Route path=":id" element={<StorageCategory currentUser={currentUser} />} />
        </Route>
        <Route path="source">
          <Route path="list" element={<SourceList currentUser={currentUser} />} />
          <Route path="add" element={<Source currentUser={currentUser} />} />
          <Route path=":id" element={<Source currentUser={currentUser} />} />
        </Route>
        <Route path="faq">
          <Route path="list" element={<FAQList currentUser={currentUser} />} />
          <Route path="add" element={<FAQ currentUser={currentUser} />} />
          <Route path=":id" element={<FAQ currentUser={currentUser} />} />
        </Route>
        <Route path="user">
          <Route path="admin" element={<AdminList currentUser={currentUser} />} />
          <Route path="admin/add" element={<Admin currentUser={currentUser} />} />
          <Route path="admin/:id" element={<Admin currentUser={currentUser} />} />
          <Route path="csr" element={<CSRList currentUser={currentUser} />} />
          <Route path="csr/add" element={<CSR currentUser={currentUser} />} />
          <Route path="csr/:id" element={<CSR currentUser={currentUser} />} />
          <Route path="all" element={<UserList currentUser={currentUser} />} />
          <Route path=":userId/partner-agreements" element={<PartnerAgreements />} />
          <Route path=":id" element={<User currentUser={currentUser} />} />
        </Route>
        <Route path="reservation">
          <Route path="list" element={<AllReservationList currentUser={currentUser} />} />
          <Route path="add" element={<AddReservation />} />
          <Route path=":id" element={<ReservationView currentUser={currentUser} />} />
          <Route path="list/:id" element={<ReservationList currentUser={currentUser} />} />
          <Route path="transaction/:id" element={<TransactionList />} />
          <Route path="payout/:id" element={<PayoutList />} />
          <Route path="refund/:id" element={<RefundList />} />
        </Route>
        <Route path="reservations">
          <Route path="add" element={<AddReservation />} />
        </Route>
        <Route path="payouts">
          <Route path="list" element={<PayoutListing currentUser={currentUser} />} />
          <Route path="view/:id" element={<PayoutDetailsView />} />
        </Route>
        <Route path="transaction">
          <Route path="list/:id" element={<RenterPayments currentUser={currentUser} />} />
        </Route>
        <Route path="transactions">
          <Route path="list" element={<AllTransactions currentUser={currentUser} />} />
        </Route>
        <Route path="reward-points">
          <Route path="list" element={<RewardPointsList currentUser={currentUser} />} />
          <Route path="view/:id" element={<RewardPointsDetailsView />} />
        </Route>
        <Route path="blocked-ip-addresses">
          <Route path="list" element={<BlockedIpAddressList currentUser={currentUser} />} />
        </Route>
        <Route path="sms-logs">
          <Route path="list" element={<SmsLogs currentUser={currentUser} />} />
          <Route path=":id" element={<SmsLogView currentUser={currentUser} />} />
        </Route>
        <Route path="payout">
          <Route path="list/:id" element={<HostPayouts currentUser={currentUser} />} />
        </Route>
        <Route path="refunds">
          <Route path="list" element={<RefundListing currentUser={currentUser} />} />
          <Route path="view/:id" element={<RefundDetailsView />} />
        </Route>
        <Route path="space">
          <Route path="list" element={<SpaceList currentUser={currentUser} />} />
          <Route path="update-prices" element={<UpdatePrices currentUser={currentUser} />} />
          <Route path="sub/spaces" element={<SubSpaces currentUser={currentUser} />} />
          <Route path="import" element={<ImportSpaces />} />
          <Route path=":id" element={<SpaceView />} />
          <Route path="edit/:id" element={<EditListing />} />
        </Route>
        <Route path="parking-inventory">
          <Route path="lots" element={<PILotList currentUser={currentUser} />} />
          <Route path="lot/add" element={<PIAddUpdateLot currentUser={currentUser} />} />
          <Route path="lot/:id" element={<PIAddUpdateLot currentUser={currentUser} />} />
          <Route path="lot/:lotId/spots" element={<PISpotList currentUser={currentUser} />} />
          <Route
            path="lot/:lotId/spot/add"
            element={<PIAddUpdateSpot currentUser={currentUser} />}
          />
          <Route
            path="lot/:lotId/spot/:spotId"
            element={<PIAddUpdateSpot currentUser={currentUser} />}
          />
          <Route
            path="lot/:lotId/spot/:spotId/detail"
            element={<PISpotDetail currentUser={currentUser} />}
          />
          <Route
            path="lot/:lotId/reservations"
            element={<PIReservationList currentUser={currentUser} />}
          />
        </Route>
      </Route>
      <Route element={<ProtectedRoutes isLoggedIn={isLoggedIn} />}>
        <Route path="leads/list" element={<LeadsTable filter="All" />} />
      </Route>
      <Route element={<PublicRoutes isLoggedIn={isLoggedIn} />}>
        <Route path="login" element={<Login />} />
        <Route
          path="forgot-password"
          element={<ForgotPassword />}
        />
        <Route
          path="reset-password"
          element={<ResetPassword />}
        />
        <Route path="/" element={<Login />} />
      </Route>
    </Routes>
  );
};

export default AppMain;
